// src/components/CookieSettings.tsx
// Cookie preferences management component for settings page

import React, { useState, useEffect } from 'react';
import {
  IonButton,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonToggle,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonContent,
  IonIcon,
  IonNote,
  IonText,
} from '@ionic/react';
import { close, shieldCheckmarkOutline } from 'ionicons/icons';
import { cookieConsentService, CookiePreferences } from '../services/cookieConsentService';

interface CookieSettingsProps {
  /** Show as modal */
  isOpen?: boolean;
  /** Called when modal is dismissed */
  onDismiss?: () => void;
  /** Called after preferences are saved */
  onSaved?: () => void;
}

export const CookieSettings: React.FC<CookieSettingsProps> = ({
  isOpen = false,
  onDismiss,
  onSaved,
}) => {
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    performance: false,
    functional: false,
  });

  useEffect(() => {
    // Load current preferences
    const consent = cookieConsentService.getConsent();
    if (consent) {
      setPreferences(consent.preferences);
    }
  }, [isOpen]); // Reload when modal opens

  const handleToggle = (category: keyof CookiePreferences, value: boolean) => {
    // Necessary cookies cannot be disabled
    if (category === 'necessary') return;

    setPreferences(prev => ({
      ...prev,
      [category]: value,
    }));
  };

  const handleSave = () => {
    cookieConsentService.saveConsent(preferences);
    onSaved?.();
    onDismiss?.();
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      performance: true,
      functional: true,
    };
    setPreferences(allAccepted);
    cookieConsentService.saveConsent(allAccepted);
    onSaved?.();
    onDismiss?.();
  };

  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      analytics: false,
      performance: false,
      functional: false,
    };
    setPreferences(onlyNecessary);
    cookieConsentService.saveConsent(onlyNecessary);
    onSaved?.();
    onDismiss?.();
  };

  const content = (
    <>
      <IonList>
        <IonListHeader>
          <IonLabel>Cookie Preferences</IonLabel>
        </IonListHeader>

        <div style={{ padding: '0 16px 16px 16px' }}>
          <IonText color="medium">
            <p style={{ fontSize: '14px', marginTop: 0 }}>
              We use cookies to enhance your experience, analyze site usage, and personalize content.
              You can manage your cookie preferences below.
            </p>
          </IonText>
        </div>

        {/* Necessary Cookies */}
        <IonItem>
          <IonLabel className="ion-text-wrap">
            <h2>Necessary Cookies</h2>
            <p>
              Required for the site to function properly. These cookies enable core features like security,
              authentication, and accessibility. They cannot be disabled.
            </p>
          </IonLabel>
          <IonToggle
            checked={true}
            disabled={true}
            slot="end"
          />
        </IonItem>

        {/* Analytics Cookies */}
        <IonItem>
          <IonLabel className="ion-text-wrap">
            <h2>Analytics Cookies</h2>
            <p>
              Help us understand how visitors interact with our site by collecting and reporting
              information anonymously. This includes error tracking and usage statistics.
            </p>
          </IonLabel>
          <IonToggle
            checked={preferences.analytics}
            onIonChange={(e) => handleToggle('analytics', e.detail.checked)}
            slot="end"
          />
        </IonItem>

        {/* Performance Cookies */}
        <IonItem>
          <IonLabel className="ion-text-wrap">
            <h2>Performance Cookies</h2>
            <p>
              Allow us to measure and improve site performance. These cookies help us identify
              slow pages and optimize load times.
            </p>
          </IonLabel>
          <IonToggle
            checked={preferences.performance}
            onIonChange={(e) => handleToggle('performance', e.detail.checked)}
            slot="end"
          />
        </IonItem>

        {/* Functional Cookies */}
        <IonItem>
          <IonLabel className="ion-text-wrap">
            <h2>Functional Cookies</h2>
            <p>
              Enable personalized features and enhanced functionality. These cookies remember
              your preferences like theme, language, and customizations.
            </p>
          </IonLabel>
          <IonToggle
            checked={preferences.functional}
            onIonChange={(e) => handleToggle('functional', e.detail.checked)}
            slot="end"
          />
        </IonItem>
      </IonList>

      <div style={{ padding: '20px', paddingBottom: '40px' }}>
        <IonButton expand="block" onClick={handleSave}>
          Save Preferences
        </IonButton>
        <IonButton expand="block" fill="outline" onClick={handleAcceptAll}>
          Accept All Cookies
        </IonButton>
        <IonButton expand="block" fill="clear" onClick={handleRejectAll}>
          Reject All (Necessary Only)
        </IonButton>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <IonNote color="medium" style={{ fontSize: '12px' }}>
            Learn more in our{' '}
            <a
              href="/docs/privacy.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--ion-color-primary)' }}
            >
              Privacy Policy
            </a>
          </IonNote>
        </div>
      </div>
    </>
  );

  // If used as inline component (not modal)
  if (!isOpen && !onDismiss) {
    return content;
  }

  // Modal version
  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Cookie Settings</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {content}
      </IonContent>
    </IonModal>
  );
};

export default CookieSettings;
